<!--
  Created 13.10.2023
  Entity cards component for the proposal show page.
  This component showcases a range of entities on the proposal page.
-->
<template>
  <div
    v-if="isCarousel"
    :class="templateCarouselClass">
    <Carousel
      ref="myCarousel"
      :wrap-around="true"
      :breakpoints="breakpointsSettings"
      class="carousel-entity"
      :class="[carouselClass, dynamicClass]"
      snap-align="start">
      <Slide
        v-for="(slide, index) in entities"
        :key="'slide-'+slide.id">
        <div class="carousel__item">
          <EntityCard
            :key="slide.id"
            :entity="slide"
            :entities-type="entitiesType"
            :entity-index="
              altsToEntityIndex.size > 0
                ? altsToEntityIndex.get(slide.id)
                : (noAltsToEntityIndex.size > 0
                  ? noAltsToEntityIndex.get(slide.id)
                  : index)
            "
            :content-editable="contentEditable"
            :string-limit="stringLimit"
            :has-no-string-limit="hasNoStringLimit"
            :is-slide="isCarousel"
            :has-white-background="hasWhiteBackground"
            :is-rounded="isRounded"
            is-vertical
            is-grouped />
        </div>
      </Slide>
      <template #addons>
        <Pagination />
      </template>
    </Carousel>
  </div>
  <div
    v-else
    class="grid grid-cols-1 gap-8"
    :class="'lg:' + gridColumnClass">
    <EntityCard
      v-for="(entity, index) in entities"
      :key="`grid-${entitiesType}-${index}-${entity.id}`"
      :entity="entity"
      :entities-type="entitiesType"
      :entity-index="
        altsToEntityIndex.size > 0
          ? altsToEntityIndex.get(entity.id)
          : (noAltsToEntityIndex.size > 0
            ? noAltsToEntityIndex.get(entity.id)
            : index)
      "
      :content-editable="contentEditable"
      :string-limit="stringLimit"
      :has-no-string-limit="hasNoStringLimit"
      :has-white-background="hasWhiteBackground"
      :is-vertical="gridColumnClass !== 'grid-cols-1'"
      is-grouped />
  </div>
</template>
<script setup>
/**
 * @component
 * Entity Cards component.
 *
 * @param {Array} entities - An array of entity objects. See Entity component for more information on the object structure.
 * @param {string} gridColumnClass - TailWind Grid Column Class, e.g. grid-cols-2.
 *
 * @returns {object}
 *
 * @example
 * <EntityCards
 *  :entities="arrayOfEntities"
 *  />
 */

/* external */
import {
  Carousel, Pagination, Slide,
} from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import {
  computed, ref,
} from 'vue';

/* local */
import EntityCard from '@/Pages/Proposal/Components/EntityCard.vue';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */

/**
 * Component Props.
 * @type {{
 *   entities: Array
 *   gridColumnClass: string
 * }}
 */
const props = defineProps({
  entities: {
    type: Array,
    required: true,
  },
  entitiesType: {
    type: String,
    required: true,
    default: '',
  },
  altsToEntityIndex: {
    type: Map,
    required: false,
    default: () => new Map(),
  },
  noAltsToEntityIndex: {
    type: Map,
    required: false,
    default: () => new Map(),
  },
  contentEditable: {
    type: Boolean,
    default: false,
  },
  stringLimit: {
    type: Number,
    required: false,
    default: 150,
  },
  hasNoStringLimit: {
    type: Boolean,
    required: false,
    default: false,
  },
  gridColumnClass: {
    type: String,
    default: 'grid-cols-2',
  },
  hasWhiteBackground: {
    type: Boolean,
    required: false,
    default: false,
  },
  isRounded: {
    type: Boolean,
    required: false,
    default: false,
  },
  isCarousel: {
    type: Boolean,
    required: false,
    default: false,
  },
  carouselBreakPoints: {
    type: Object,
    required: false,
    default: () => ({
      default: null,
      sm: null,
      md: null,
      lg: null,
      xl: null,
      '2xl': null,
    }),
  },
  carouselBleed: {
    type: String,
    required: false,
    default: '',
    validator: (value) => ['full', 'left', 'right'].includes(value),
  },
});

const myCarousel = ref(null);

const carouselBleedMap = {
  full: {
    templateCarousel: 'sm:-mx-6',
    carousel: 'sm:mx-break-out',
  },
  left: {
    templateCarousel: 'sm:-ml-6',
    carousel: 'sm:ml-break-out',
  },
  right: {
    templateCarousel: 'sm:-mr-6',
    carousel: 'sm:mr-break-out',
  },
};

const templateCarouselClass = computed(() => carouselBleedMap[props.carouselBleed]?.templateCarousel || '');
const carouselClass = computed(() => carouselBleedMap[props.carouselBleed]?.carousel || '');

const breakpointsSettings = {
  // default (mobile first)
  0: {
    itemsToShow: props.carouselBreakPoints.default ?? 1,
  },
  // tailwind 'sm' (and up)
  640: {
    itemsToShow: props.carouselBreakPoints.sm ?? 1.25,
  },
  // md
  768: {
    itemsToShow: props.carouselBreakPoints.md ?? 1.25,
  },
  // lg
  1024: {
    itemsToShow: props.carouselBreakPoints.lg ?? 2.25,
  },
  // xl
  1280: {
    itemsToShow: props.carouselBreakPoints.xl ?? 2.25,
  },
  // 2xl
  1536: {
    itemsToShow: props.carouselBreakPoints['2xl'] ?? 3.25,
  },
};

// Check if image carousel only has one item visible
const allValuesAreOne = ref(false);
allValuesAreOne.value = Object.values(props.carouselBreakPoints).every((value) => value === 1);
const dynamicClass = computed(() => (allValuesAreOne.value ? 'condition-true' : 'condition-false'));

/* *********************************************************************
 * Component functions
 * ****************************************************************** */

const nextSlide = () => {
  myCarousel.value.next();
};

defineExpose({
  nextSlide,
});

</script>
<style lang="scss" scoped>
:deep(.carousel.condition-false) {
  @media (min-width: 640px) {
    .carousel__slide {
      padding-left: 1rem;
      padding-right: 1rem;
      transition: padding-left 0.1s 0.1s, padding-right 0.1s 0.1s, margin-right 0.1s 0.1s; /* duration delay */
    }
    .carousel__slide--active {
      padding-left: 0;
      padding-right: 2rem;
      margin-right: -1rem;
    }
  }
}
:deep(.carousel) {
  .carousel__item {
    height: 100%;
  }
  .carousel__prev,
  .carousel__next {
    background: none;
    color: rgb(31 41 55 / 0.5);
    top: initial;
    bottom: 0.9rem;
    transform: none;
    &:hover {
      color: rgb(31 41 55 / 1);
    }
  }
  .carousel__prev {
    left: 0;
  }
  .carousel__next {
    right: 0;
  }
  .carousel__pagination {
    margin-top: 0.5rem;
  }
  .carousel__pagination-button {
    height: 2px;
    display: block;
  }
}
</style>
